import { ELLIOTT_BAY } from "./App";

// Fetch detailed wind/weather for a race date (~6pm local) from Open-Meteo (free, no key)
export async function getRaceWeather(dateISO) {
  const url = `https://api.open-meteo.com/v1/forecast?latitude=${ELLIOTT_BAY.lat}&longitude=${ELLIOTT_BAY.lon}` +
    `&hourly=wind_speed_10m,wind_gusts_10m,wind_direction_10m,temperature_2m,apparent_temperature,precipitation_probability,weather_code` +
    `&wind_speed_unit=kn&temperature_unit=fahrenheit&timezone=America/Los_Angeles` +
    `&start_date=${dateISO}&end_date=${dateISO}&_t=${Date.now()}`;

  const res = await fetch(url, { cache: "no-store" });
  if (!res.ok) throw new Error("weather unavailable");
  const data = await res.json();

  const times = data.hourly?.time || [];
  const idx = times.findIndex(t => t.endsWith("19:00"));
  const i = idx >= 0 ? idx : Math.floor(times.length / 2);

  const deg = data.hourly.wind_direction_10m[i];
  return {
    windSpeed: Math.round(data.hourly.wind_speed_10m[i]),
    windGust: Math.round(data.hourly.wind_gusts_10m[i]),
    windDir: Math.round(deg),
    windCompass: degToCompass(deg),
    temp: Math.round(data.hourly.temperature_2m[i]),
    feelsLike: Math.round(data.hourly.apparent_temperature[i]),
    precip: Math.round(data.hourly.precipitation_probability[i] ?? 0),
    condition: weatherCodeToText(data.hourly.weather_code[i]),
    conditionIcon: weatherCodeToIcon(data.hourly.weather_code[i]),
    weatherCode: data.hourly.weather_code[i],
  };
}

function degToCompass(deg) {
  const dirs = ["N","NNE","NE","ENE","E","ESE","SE","SSE","S","SSW","SW","WSW","W","WNW","NW","NNW"];
  return dirs[Math.round(deg / 22.5) % 16];
}

function weatherCodeToText(code) {
  if (code === 0) return "Clear";
  if (code <= 2) return "Partly cloudy";
  if (code === 3) return "Overcast";
  if (code <= 48) return "Foggy";
  if (code <= 67) return "Rainy";
  if (code <= 77) return "Snow";
  if (code <= 82) return "Showers";
  if (code <= 99) return "Storms";
  return "—";
}

function weatherCodeToIcon(code) {
  if (code === 0) return "☀️";
  if (code <= 2) return "🌤️";
  if (code === 3) return "☁️";
  if (code <= 48) return "🌫️";
  if (code <= 67) return "🌧️";
  if (code <= 77) return "❄️";
  if (code <= 82) return "🌦️";
  if (code <= 99) return "⛈️";
  return "🌤️";
}

// Get a short weather summary string for a race date (works for past dates via forecast API,
// which covers recent past ~3 months). Returns "" on failure so the narrative can still proceed.
export async function getWeatherSummary(dateISO) {
  try {
    const w = await getRaceWeather(dateISO);
    return `Wind ${w.windSpeed}kt ${w.windCompass} (gusts ${w.windGust}kt), ${w.temp}°F, ${w.condition}`;
  } catch (e) {
    return "";
  }
}

// Fetch just the race-day temperature (°F) at ~7pm for a past or upcoming date.
// Uses the forecast endpoint (covers recent past + future ~16 days); falls back to archive for older dates.
export async function getRaceTemp(dateISO) {
  const base = `latitude=${ELLIOTT_BAY.lat}&longitude=${ELLIOTT_BAY.lon}&hourly=temperature_2m&temperature_unit=fahrenheit&timezone=America/Los_Angeles&start_date=${dateISO}&end_date=${dateISO}`;
  const tryUrl = async (url) => {
    const res = await fetch(url);
    if (!res.ok) throw new Error("temp unavailable");
    const data = await res.json();
    const times = data.hourly?.time || [];
    const idx = times.findIndex(t => t.endsWith("19:00"));
    const i = idx >= 0 ? idx : Math.floor(times.length / 2);
    const t = data.hourly?.temperature_2m?.[i];
    if (t === undefined || t === null) throw new Error("no temp");
    return Math.round(t);
  };
  try {
    return await tryUrl(`https://api.open-meteo.com/v1/forecast?${base}`);
  } catch (e) {
    // Older than forecast window — use archive
    return await tryUrl(`https://archive-api.open-meteo.com/v1/archive?${base}`);
  }
}
